// FIX-CCS-B1-APP-HALF — wait, briefly, for the server-side photo finding.
//
// Since unit B1 the app makes ONE call (upload-photo) and the server starts the analysis in the
// background. Adeel's ruling (29 Sep 2026): the app waits up to 20 s for the finding, reading the
// photo row every 2 s through the farmer's own session read path, and otherwise shows the generic
// "still checking" message. Polling stops the moment the screen (or the modal) closes.
//
// This module has no app imports so it can be unit-tested with an injected reader and clock.
// It makes exactly one read per tick and never retries a failed read inside a tick — a blocked
// read costs at most 10 requests over the whole 20 s window.

export interface FarmerFinding {
  /** 'unclear' = the server's photo check failed or the status is missing/unknown (BUILD-PHOTO-CHECK-PROMPT). */
  status: 'excellent' | 'good' | 'fair' | 'stressed' | 'critical' | 'unclear';
  main_finding: string;
  action: 'none' | 'see_agriagent' | 'see_community' | 'contact_supervisor';
  next_check: 'today' | '3_days' | '1_week' | 'next_cycle';
}

/** Reads the photo's `ai_analysis` JSON (null while the analysis has not landed). Must throw on a failed read. */
export type ReadAnalysis = (photoId: string) => Promise<unknown>;

export interface PollOptions {
  /** Time between reads. Default 2000 ms. */
  intervalMs?: number;
  /** Total window measured from the start. Default 20000 ms. */
  timeoutMs?: number;
  /** Injectable clock for tests. */
  now?: () => number;
  /** Injectable sleeper for tests; must resolve early when `signal.cancelled` is set. */
  wait?: (ms: number, signal: { cancelled: boolean }) => Promise<void>;
}

export interface PollOutcome {
  /** The farmer-facing finding, or null when the window closed, the poll was cancelled, or the analysis has no farmer part. */
  finding: FarmerFinding | null;
  /** 'finding' | 'no_farmer_part' | 'timeout' | 'cancelled' */
  reason: 'finding' | 'no_farmer_part' | 'timeout' | 'cancelled';
  /** How many reads were attempted (successful or failed). */
  reads: number;
  /** How many reads threw. */
  failedReads: number;
}

export interface PollHandle {
  promise: Promise<PollOutcome>;
  cancel: () => void;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const STATUSES: FarmerFinding['status'][] = ['excellent', 'good', 'fair', 'stressed', 'critical', 'unclear'];
const ACTIONS: FarmerFinding['action'][] = ['none', 'see_agriagent', 'see_community', 'contact_supervisor'];
const NEXT_CHECKS: FarmerFinding['next_check'][] = ['today', '3_days', '1_week', 'next_cycle'];

/**
 * Maps a photos.ai_analysis value to the farmer-facing finding the camera modal shows.
 * Returns null when there is no `for_farmer` part. A missing or unknown status is 'unclear'
 * (BUILD-PHOTO-CHECK-PROMPT: never 'good' by default); action / next_check keep the pre-B1
 * defaults ('none' / 'next_cycle').
 */
export function toFarmerFinding(aiAnalysis: unknown): FarmerFinding | null {
  if (!isRecord(aiAnalysis)) return null;
  const farmer = aiAnalysis.for_farmer;
  if (!isRecord(farmer)) return null;
  const status = STATUSES.includes(farmer.status as FarmerFinding['status']) ? (farmer.status as FarmerFinding['status']) : 'unclear';
  const action = ACTIONS.includes(farmer.action as FarmerFinding['action']) ? (farmer.action as FarmerFinding['action']) : 'none';
  const nextCheck = NEXT_CHECKS.includes(farmer.next_check as FarmerFinding['next_check']) ? (farmer.next_check as FarmerFinding['next_check']) : 'next_cycle';
  return {
    status,
    main_finding: typeof farmer.main_finding === 'string' ? farmer.main_finding : '',
    action,
    next_check: nextCheck,
  };
}

const realWait = (ms: number, signal: { cancelled: boolean }) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms);
    // A cancelled poll should not keep the timer alive; poll() checks `signal.cancelled` after every wait.
    const tick = setInterval(() => { if (signal.cancelled) { clearTimeout(t); clearInterval(tick); resolve(); } }, 100);
    setTimeout(() => clearInterval(tick), ms);
  });

/**
 * Reads the photo every `intervalMs` until `ai_analysis` appears, the window closes, or `cancel()` is called.
 * Reads happen at 2 s, 4 s … 20 s (10 reads by default). A read that throws is ignored until the next tick.
 */
export function pollForFinding(photoId: string, read: ReadAnalysis, opts: PollOptions = {}): PollHandle {
  const intervalMs = opts.intervalMs ?? 2000;
  const timeoutMs = opts.timeoutMs ?? 20000;
  const now = opts.now ?? (() => Date.now());
  const wait = opts.wait ?? realWait;
  const signal = { cancelled: false };
  let reads = 0;
  let failedReads = 0;

  const promise = (async (): Promise<PollOutcome> => {
    const start = now();
    while (!signal.cancelled) {
      await wait(intervalMs, signal);
      if (signal.cancelled) break;
      if (now() - start > timeoutMs) return { finding: null, reason: 'timeout', reads, failedReads };
      reads += 1;
      let analysis: unknown = null;
      try {
        analysis = await read(photoId);
      } catch {
        failedReads += 1;
        continue;
      }
      if (signal.cancelled) break;
      if (analysis !== null && analysis !== undefined) {
        const finding = toFarmerFinding(analysis);
        return { finding, reason: finding ? 'finding' : 'no_farmer_part', reads, failedReads };
      }
    }
    return { finding: null, reason: 'cancelled', reads, failedReads };
  })();

  return { promise, cancel: () => { signal.cancelled = true; } };
}
